import { Component, Input, OnInit, Output, EventEmitter, SimpleChanges } from '@angular/core';
import { Utils } from 'src/app/services';

@Component({
  selector: 'chart',
  templateUrl: './chart.component.html',
  styleUrls: ['./chart.component.less']
})
export class ChartComponent implements OnInit {
  @Input() title: string | undefined;
  @Input() subtitle: boolean = false;
  @Input() type: string = "bar";
  @Input() disablePie: boolean = true;
  @Input() loading: boolean = false;
  @Input() options: any | undefined;
  @Input() labels: string[] = [];
  @Input() legend = true;
  @Input() data: any[] = [];
  @Input() colors: Array<string> = [];
  @Input() isDarkMode: boolean = false;
  @Input() slimMode: boolean = false;
  @Input() fillHeight: boolean = false;
  @Input() labelFormatter: Function | undefined;
  @Input() useCardWrapper: boolean = true;
  @Output() chartClick: EventEmitter<string> = new EventEmitter();

  chartOptions: any = {};
  chartColors: Array<any> = [];

  private blackRgba = "rgba(255, 255, 255, 1)";
  private whiteRgba = "rgba(0, 0, 0, 1)";

  constructor() {
    this.setOptions();
    if (this.options) {
      this.chartOptions = this.options;
    }
  }

  ngOnChanges(changes: SimpleChanges) { 
    if (changes.isDarkMode || changes.colors) {
      this.setOptions();
    }
  }

  ngOnInit(): void {}
  
  setChartType(chartType: string): void {
    this.type = chartType;
    this.setOptions();
  }

  private handleChartClick(event: any, legendItem: any) {
    if (this.type === 'pie' || this.type === 'doughnut') {
      if (legendItem && legendItem.length > 0) {
        const clickedIndex = legendItem[0]._index;
        const label = this.labels[clickedIndex];
        this.chartClick.emit(label);
      }
    } else {
      if (legendItem[0]?._model?.label) {
        this.chartClick.emit(legendItem[0]._model.label);
      }
    }
  }

  defaultLabelFormatter(tick: string) {
    return tick;
  }

  private setOptions() {
    this.chartOptions = [];
    this.chartColors = [];
    
    switch (this.type) {
      case "pie":
      case "doughnut":
        this.chartOptions = {
          responsive: true,
          maintainAspectRatio: true,
          onClick: (event: any, legendItem: any) => {
            this.handleChartClick(event, legendItem);
          },
          legend: {
            position: 'bottom',
            labels: {
              fontColor: this.isDarkMode ? "rgba(255, 255, 255, 0.8)" : "rgba(0, 0, 0, 0.8)"
            }
          },
          tooltips: {
            callbacks: {
              label: (tooltipItem: any, data: any) => {
                const dataset = data.datasets[tooltipItem.datasetIndex];
                const total = dataset.data.reduce((previousValue: number, currentValue: number) => previousValue + currentValue);
                const currentValue = dataset.data[tooltipItem.index];
                const percentage = Math.floor(((currentValue/total) * 100)+0.5);
                return `${data.labels[tooltipItem.index]}: ${currentValue} (${percentage}%)`;
              }
            }
          }
        };
        
        // For pie charts, we need different color handling
        const backgroundColors = [];
        for (let i = 0; i < (this.data[0]?.data?.length || 0); i++) {
          const colorIndex = i % this.colors.length;
          backgroundColors.push(this.colors[colorIndex]);
        }
        
        this.chartColors = [{
          backgroundColor: backgroundColors,
          borderColor: this.isDarkMode ? "rgba(48, 48, 48, 1)" : "rgba(255, 255, 255, 1)",
          borderWidth: 1
        }];
        break;
        
      case "line":
        this.chartOptions = { 
          responsive: true,
          maintainAspectRatio: true,
          onClick: (event: any, legendItem: any) => {
            this.handleChartClick(event, legendItem);
          },
          legend: {
            labels: {
              fontColor: this.isDarkMode ? "rgba(255, 255, 255, 0.8)" : "rgba(0, 0, 0, 0.8)" 
            }
          },
          scales: {
            yAxes: [{
              ticks: {
                beginAtZero: true,
                fontColor: this.isDarkMode ? "rgba(255, 255, 255, 0.8)" : "rgba(0, 0, 0, 0.8)" 
              }
            }],
            xAxes: [{
              ticks: {
                fontColor: this.isDarkMode ? "rgba(255, 255, 255, 0.8)" : "rgba(0, 0, 0, 0.8)",
                callback: this.labelFormatter ? this.labelFormatter : this.defaultLabelFormatter
              }
            }]
          }
        };
        this.colors.forEach(color => {
          this.chartColors.push({
            backgroundColor: Utils.changeRgbaOpacity(color, "0.3"),
            borderColor: Utils.changeRgbaOpacity(color, "0.8"),
            pointBackgroundColor: color,
            pointBorderColor: this.isDarkMode ? Utils.changeRgbaOpacity(this.blackRgba, "0.8") : "rgba(0, 0, 0, 0.8)",
            pointHoverBackgroundColor: this.isDarkMode ? "rgba(255, 255, 255, 0.8)" : "rgba(0, 0, 0, 0.8)",
            pointHoverBorderColor: 'rgba(25,118,210,0.5)'
          });
        });
        break;
        
      case "horizontalBar":
        this.chartOptions = { 
          scaleShowVerticalLines: false,
          responsive: true,
          maintainAspectRatio: true,
          onClick: (event: any, legendItem: any) => {
            this.handleChartClick(event, legendItem);
          },
          legend: {
            display: false // Hide legend for horizontal bar
          },
          scales: {
            xAxes: [{
              ticks: {
                beginAtZero: true,
                fontColor: this.isDarkMode ? "rgba(255, 255, 255, 0.8)" : "rgba(0, 0, 0, 0.8)"
              },
              gridLines: {
                color: this.isDarkMode ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)"
              }
            }],
            yAxes: [{
              ticks: {
                fontColor: this.isDarkMode ? "rgba(255, 255, 255, 0.8)" : "rgba(0, 0, 0, 0.8)",
                callback: this.labelFormatter ? this.labelFormatter : this.defaultLabelFormatter
              },
              gridLines: {
                display: false
              }
            }]
          }
        };
        
        // For horizontal bar charts, use individual colors for each bar
        const hBarBackgroundColors = [];
        for (let i = 0; i < (this.data[0]?.data?.length || 0); i++) {
          const colorIndex = i % this.colors.length;
          hBarBackgroundColors.push(this.colors[colorIndex]);
        }
        
        this.chartColors = [{
          backgroundColor: hBarBackgroundColors,
          borderColor: this.isDarkMode ? "rgba(48, 48, 48, 1)" : "rgba(255, 255, 255, 1)",
          borderWidth: 1
        }];
        break;
        
        default: // bar chart and others
        this.chartOptions = { 
          scaleShowVerticalLines: false,
          responsive: true,
          maintainAspectRatio: true,
          barThickness: 40, // Increase from 10 to make bars wider
          maxBarThickness: 100,
          onClick: (event: any, legendItem: any) => {
            this.handleChartClick(event, legendItem);
          },
          legend: {
            labels: {
              fontColor: this.isDarkMode ? "rgba(255, 255, 255, 0.8)" : "rgba(0, 0, 0, 0.8)" 
            }
          },
          scales: {
            yAxes: [{
              ticks: {
                beginAtZero: true,
                fontColor: this.isDarkMode ? "rgba(255, 255, 255, 0.8)" : "rgba(0, 0, 0, 0.8)" 
              }
            }],
            xAxes: [{
              // barPercentage: 0.9,
              // categoryPercentage: 0.9,
              ticks: {
                fontColor: this.isDarkMode ? "rgba(255, 255, 255, 0.8)" : "rgba(0, 0, 0, 0.8)",
                callback: this.labelFormatter ? this.labelFormatter : this.defaultLabelFormatter,
                backgroundColors: this.chartColors
              }
            }]
          }
        };
        this.colors.forEach(color => {
          this.chartColors.push({ 
            backgroundColor: color,
            maxBarThickness: 100
          });
        });
        break;
    }
  }
}